/**
 * Helpers for the email forwarding-account admin screen.
 *
 * The backend lowercases and trims `account_email`, rejects the user's own
 * login email, rejects duplicates and caps the list at `max_accounts`. These
 * helpers mirror those rules client-side so the admin gets instant feedback
 * instead of a round-trip error.
 */

/** Same normalisation the backend applies (`trim().toLowerCase()`). */
export function normalizeEmail(value) {
  const trimmed = typeof value === "string" ? value.trim().toLowerCase() : "";
  return trimmed || null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value) {
  const normalized = normalizeEmail(value);
  return Boolean(normalized) && EMAIL_PATTERN.test(normalized);
}

/**
 * Validate a new forwarding-account email against the current list state.
 *
 * @param {string} email raw input
 * @param {{ login_email?: string|null, accounts?: Array<{account_email: string}>, max_accounts?: number }} summary
 * @returns {string|null} error message, or null when the email is acceptable
 */
export function validateNewForwardingEmail(email, summary) {
  const normalized = normalizeEmail(email);
  if (!normalized) return "Enter an email address to forward from.";
  if (!isValidEmail(normalized)) return "Enter a valid email address.";

  if (normalizeEmail(summary?.login_email) === normalized) {
    return "The login email is always included and does not need to be added.";
  }

  const accounts = Array.isArray(summary?.accounts) ? summary.accounts : [];
  if (accounts.some((a) => normalizeEmail(a?.account_email) === normalized)) {
    return "This forwarding account is already added.";
  }

  const max = summary?.max_accounts;
  if (typeof max === "number" && accounts.length >= max) {
    return `You can add at most ${max} forwarding accounts.`;
  }

  return null;
}

/** Unwrap `{ data: { … } }` / bare payloads into the list summary shape. */
export function parseForwardingAccountsResponse(payload) {
  const data = payload?.data ?? payload;
  const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
  return {
    login_email: normalizeEmail(data?.login_email),
    max_accounts: typeof data?.max_accounts === "number" ? data.max_accounts : null,
    count: typeof data?.count === "number" ? data.count : accounts.length,
    accounts,
  };
}

/**
 * Group picker users into org sections for the master list, applying a
 * free-text filter across name, email and org name.
 *
 * @param {ReturnType<import("./fetchOrgUsers").flattenOrgUsersForPicker>} users
 * @param {string} search
 * @returns {Array<{ orgName: string, users: object[] }>}
 */
export function groupUsersByOrg(users, search = "") {
  const term = String(search || "").trim().toLowerCase();
  const list = Array.isArray(users) ? users : [];

  const matched = term
    ? list.filter((u) =>
        [u?.name, u?.email, u?.orgName].some((v) =>
          String(v || "").toLowerCase().includes(term)
        )
      )
    : list;

  const byOrg = new Map();
  for (const user of matched) {
    const orgName = user?.orgName || "Other";
    if (!byOrg.has(orgName)) byOrg.set(orgName, []);
    byOrg.get(orgName).push(user);
  }

  return Array.from(byOrg.entries())
    .map(([orgName, orgUsers]) => ({
      orgName,
      users: orgUsers
        .slice()
        .sort((a, b) =>
          String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""))
        ),
    }))
    .sort((a, b) => a.orgName.localeCompare(b.orgName));
}

/**
 * Rank picker users for the search-as-you-type combobox.
 *
 * Returns nothing for an empty term — the picker shows no list until the admin
 * types, so a large directory never dumps hundreds of rows on screen. Matches
 * that start with the term rank above mid-string matches.
 *
 * @param {ReturnType<import("./fetchOrgUsers").flattenOrgUsersForPicker>} users
 * @param {string} search
 * @param {number} [limit] maximum suggestions to return
 * @returns {object[]}
 */
export function searchUsers(users, search = "", limit = 8) {
  const term = String(search || "").trim().toLowerCase();
  if (!term) return [];

  const list = Array.isArray(users) ? users : [];
  const scored = [];

  for (const user of list) {
    const fields = [user?.name, user?.email, user?.orgName].map((v) =>
      String(v || "").toLowerCase()
    );
    let best = Infinity;
    for (let i = 0; i < fields.length; i += 1) {
      const at = fields[i].indexOf(term);
      if (at === -1) continue;
      // Prefix hits beat mid-string hits; name beats email beats org.
      best = Math.min(best, (at === 0 ? 0 : 100) + i);
    }
    if (best !== Infinity) scored.push({ user, score: best });
  }

  return scored
    .sort(
      (a, b) =>
        a.score - b.score ||
        String(a.user.name || a.user.email || "").localeCompare(
          String(b.user.name || b.user.email || "")
        )
    )
    .slice(0, Math.max(0, limit))
    .map((entry) => entry.user);
}

/**
 * Split pasted text (comma, semicolon, whitespace or newline separated) into
 * normalised, de-duplicated emails, keeping malformed entries apart so the
 * form can flag them before submitting.
 *
 * @param {string} text
 * @returns {{ valid: string[], invalid: string[] }}
 */
export function parseEmailList(text) {
  const tokens = String(text || "")
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];
  const seen = new Set();
  for (const token of tokens) {
    const normalized = normalizeEmail(token);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    (isValidEmail(normalized) ? valid : invalid).push(normalized);
  }
  return { valid, invalid };
}

/**
 * True when a bulk-add result means the user already had this account — the
 * end state the admin wanted, so the UI treats it as a success, not an error.
 */
export function isAlreadyAdded(result) {
  return result?.status !== "added" && /already/i.test(String(result?.reason || ""));
}

/**
 * Unwrap the bulk POST /email/forwarding-accounts response:
 * `{ results: [{ user_index, status, account?, reason? }], not_found: string[] }`.
 * A legacy single-account body is treated as one "added" result.
 */
export function parseBulkAddResponse(payload) {
  const data = payload?.data ?? payload;
  if (!Array.isArray(data?.results) && data?.account_email) {
    return { results: [{ user_index: null, status: "added", account: data }], notFound: [] };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    notFound: Array.isArray(data?.not_found) ? data.not_found.map(String) : [],
  };
}
